/*
  assets/nibec-cotizacion-formal.js · Bigbuda T-0449 (14-oct-2026)
  Cotización formal en 2 pasos del formulario de /pages/contacto (sections/nibec-contacto-form.liquid,
  ajuste «Cotización formal en 2 pasos»). Pedido de Fernando y Benjamín: volver al formulario con los
  datos necesarios para generar la cotización en Bsale.

  Qué hace:
    · Paso 1 (nombre, correo, teléfono, producto y cantidad) y paso 2 (RUT, razón social, comuna y dirección
      de despacho) son bloques del MISMO formulario: un solo envío nativo de Shopify, el mismo correo a atención
      al cliente y el mismo mensaje de éxito (data-brevo-exito → GA4 generate_lead). Este archivo solo muestra de
      a un bloque, valida y, al enviar, ordena el RUT y arma la copia de dirección para Brevo.
    · «Continuar» valida el paso 1 y pasa al 2; «Volver» regresa sin perder lo escrito. Enter en el paso 1 hace lo
      mismo que «Continuar».
    · RUT con formato chileno: acepta 12345678-5, 12.345.678-5 o 123456785 (con K), comprueba el dígito
      verificador (módulo 11) y lo deja escrito como 12.345.678-5.
    · Captcha: el envío de Shopify pasa por hCaptcha; la validación de aquí corre antes (captura en window) y, si bloquea,
      corta el evento entero para que el captcha no arranque.
    · Brevo: nibec-brevo.js lee cada campo por su data-brevo. Aquí solo se usan claves que el Worker ya entiende
      (rut y direccion = «dirección, comuna»). La razón social y la comuna sueltas llegan al correo, no a Brevo.
    · Medición: el botón de envío sigue siendo hijo directo del formulario (form#contact_form > button, el selector
      del evento «Enviar formulario» de Heatmap); los pasos publican nibec_cotizacion_paso (dataLayer y
      Shopify.analytics) y los botones nuevos llevan data-hm-id.

  Ajuste «Campos Giro y Empresa/Persona» (17-oct-2026, propuesta para Benjamín; apagado por defecto): el paso 2 suma «¿Persona natural o
  empresa?» (radios nativos) y «Giro». Este archivo cambia la etiqueta y el texto de ayuda de «Razón social o nombre» según la
  elección, muestra «Giro» (obligatorio) solo para empresa y valida el grupo de radios. Sin esos campos no hace nada distinto.

  T-1552 (28-oct-2026, pedido de Benjamín Canales de Nibec):
    · Los PRODUCTOS son obligatorios. El producto ya no se pide escribiendo en el formulario: sin al menos un producto en la lista de cotización
      (nibec-cotizacion.js) «Continuar» y «Cotizar» no avanzan; se muestra un error con foco en el estado vacío (tarjeta «Tu cotización» de la columna
      derecha en computador, bloque `[data-nibec-productos-vacio]` dentro del formulario en celular y tablet). El texto de «Información adicional» es opcional.
    · Cómo necesita recibir los productos (obligatorio) en el paso 2, con las opciones de Benjamín: despacho en Santiago, armado en Santiago, retiro en tienda y
      despacho a Región (radios en T-1552; casillas desde T-1582, ver abajo). Solo con «Retiro en tienda» NO se piden dirección ni comuna: esos dos campos se ocultan y se
      deshabilitan (no se validan ni viajan en el correo). La elección sale en el correo como «Entrega: <opción>» y, como `entrega` (código), en el campo oculto
      data-brevo="entrega" hacia el Worker de formularios (que la pasa a Versu; Brevo no la usa).
    · Mientras la lista está vacía, «Continuar» y «Cotizar» se ven apagados (clase is-sin-productos; NO están deshabilitados ni llevan aria-disabled: el lector de
      pantalla no los anuncia como no disponibles, reciben el foco y al apretarlos sale el error) y un aviso junto al botón dice por qué (aria-describedby).

  T-1552 (28-oct-2026, 2.ª parte: avisos que ayudan a avanzar, no a frenar; directriz de Marcel: «debe ser para convertir más, no para frenar más»):
    · Sin productos, «Continuar» y «Cotizar» NUNCA bloquean en silencio: junto al botón (computador) o en la tarjeta justo sobre la barra del botón (celular) sale
      «Para continuar, agrega al menos un producto a tu cotización.» con la salida al lado («Explorar productos»). Si la página no trae ese bloque (el JS llegó a
      Shopify antes que la sección), este archivo crea el mismo aviso junto al botón: nunca queda un bloqueo mudo.
    · En computador el aviso es una región en vivo (role="status"): se anuncia sin mover el foco del botón. En celular el foco va a la línea de error de la tarjeta.
    · Los errores de campo y el de «Entrega» se llevan a la vista por encima de la barra fija del botón (antes quedaban tapados por ella en celular).
    · La salida de un aviso puede llevar a otra página («Explorar productos»): lo escrito en el paso 1 se guarda en la pestaña (sessionStorage) y vuelve al
      regresar; se borra al enviar de verdad. Nada sale del navegador.

  T-1582 (28-oct-2026, pedido de Benjamín: «si pongo armado dentro de Stgo me quita el despacho. Y se cobran ambos»; Marcel: «el formulario debe ser para convertir
  más, no para frenar más»): las opciones de entrega pasan de radios a CASILLAS (checkbox) con estas reglas, que viven aquí:
    · «Despacho en Santiago» y «Armado en Santiago» van juntas: al marcar el armado se marca solo el despacho y queda marcada y bloqueada (aria-disabled: sigue
      recibiendo el foco y un lector de pantalla dice por qué) mientras el armado esté marcado; con solo el despacho, el armado queda sin marcar.
    · «Retiro en tienda» y «Despacho a Región» son excluyentes entre sí y con las dos de Santiago: marcar una desmarca las otras (y se anuncia).
    · Al menos una es obligatoria (mismo aviso de siempre). Con retiro no se piden dirección ni comuna.
    · Las casillas no viajan (sin `name`): la elección sale en el campo oculto contact[Entrega] con el texto legible (correo: «Entrega: …») y en data-brevo="entrega"
      con el código para el Worker: despacho_santiago | despacho_armado_santiago (despacho y armado juntos) | retiro_tienda | despacho_region.
    · Compatible con la sección anterior (radios `data-entrega`, incluido el código `armado_santiago`): si la página trae radios, todo funciona como antes.

  T-2032 (3-nov-2026, pedido de Benjamín Canales: «los campos de la solicitud deben ser obligatorios… con retiro en tienda no pedimos dirección y comuna, las necesito
  para enviar cotización»). Con el ajuste «Datos obligatorios en la cotización» (Configuración del tema › Nibec · Cotización única; el formulario trae
  data-nibec-obligatorios="true"):
    · Con «Retiro en tienda» TAMBIÉN se piden dirección y comuna (ya no se ocultan ni se deshabilitan); la nota bajo las opciones explica por qué. «Datos obligatorios»
      reemplaza la regla de T-1552/T-1582 «con retiro no se piden dirección ni comuna».
    · Se revisan los datos, no solo que no estén vacíos: RUT con dígito verificador (ya estaba), dirección (5 caracteres o más, con letras), comuna (solo letras),
      razón social y giro (3 o más). Los mensajes dicen qué falta y cómo corregirlo.
    · Medición: cada error mostrado publica nibec_cotiza_error_campo (campo, motivo vacio|formato, origen envio|salida, paso, dispositivo, entrega) en Shopify.analytics
      y dataLayer. Sin datos personales: solo el nombre del campo.
  Apagado el ajuste, todo queda como antes (retiro sin dirección ni comuna, mismos textos, sin evento).

  Sin JavaScript no pasa nada raro: se ven los dos bloques seguidos y el envío nativo valida con `required` (las casillas de entrega y la lista de productos
  necesitan JavaScript, como el resto del cotizador).
*/
(function () {
  'use strict';

  // Borrador del paso 1 (ver T-1552 2.ª parte): si la página vuelve del envío exitoso ya no se necesita.
  var BORRADOR = 'nibecContactoBorrador';
  try {
    if (document.querySelector('[data-brevo-exito]')) sessionStorage.removeItem(BORRADOR);
  } catch (e) {}

  var form = document.getElementById('contact_form');
  if (!form || form.getAttribute('data-nibec-formal') === 'listo') return;

  var p1 = form.querySelector('[data-paso="1"]');
  var p2 = form.querySelector('[data-paso="2"]');
  var siguiente = form.querySelector('[data-nibec-continuar]');
  var volver = form.querySelector('[data-nibec-volver]');
  var enviar = form.querySelector('button[type="submit"]');
  var rut = form.querySelector('[data-nibec-rut]');
  if (!p1 || !p2 || !siguiente || !volver || !enviar || !rut) return;

  // T-2032: «Datos obligatorios en la cotización» encendido en el editor del tema.
  var OBL = form.getAttribute('data-nibec-obligatorios') === 'true';

  form.setAttribute('data-nibec-formal', 'listo');
  form.classList.add('is-pasos');
  // Los mensajes los pone este archivo (con foco y aria-describedby); los globos del navegador no.
  form.noValidate = true;

  var actual = 1;
  var reducir = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var CORREO = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // el mismo que usa el Worker de Brevo

  // ---- RUT chileno -------------------------------------------------------------------------------------------
  function limpiarRut(v) {
    return String(v || '').replace(/[^0-9kK]/g, '').toUpperCase();
  }
  function digitoVerificador(cuerpo) {
    var suma = 0;
    var mult = 2;
    for (var i = cuerpo.length - 1; i >= 0; i--) {
      suma += parseInt(cuerpo.charAt(i), 10) * mult;
      mult = mult === 7 ? 2 : mult + 1;
    }
    var r = 11 - (suma % 11);
    return r === 11 ? '0' : r === 10 ? 'K' : String(r);
  }
  function rutValido(v) {
    var c = limpiarRut(v);
    if (c.length < 8 || c.length > 9) return false; // 7 u 8 dígitos y el verificador
    var cuerpo = c.slice(0, -1);
    // desde 1.000.000: deja fuera 0-0, 1-9 y otros inventados que también cumplen el módulo 11
    return /^[0-9]+$/.test(cuerpo) && parseInt(cuerpo, 10) >= 1000000 && digitoVerificador(cuerpo) === c.slice(-1);
  }
  function formatearRut(v) {
    var c = limpiarRut(v);
    if (c.length < 2) return c;
    return c.slice(0, -1).replace(/\B(?=(\d{3})+(?!\d))/g, '.') + '-' + c.slice(-1);
  }

  // ---- Errores ----------------------------------------------------------------------------------------------
  // Los campos que se validan: sin los deshabilitados (p. ej. «Giro» de una persona natural) y con un solo radio por grupo.
  function campos(paso) {
    var vistos = {};
    return Array.prototype.slice
      .call(paso.querySelectorAll('input:not([type="hidden"]), textarea'))
      .filter(function (c) {
        if (c.disabled) return false;
        if (c.type === 'radio') {
          if (vistos[c.name]) return false;
          vistos[c.name] = true;
        }
        // T-1582: las casillas de entrega son UN solo grupo (se exige al menos una): se valida y se enfoca la primera.
        if (c.type === 'checkbox' && c.closest && c.closest('[data-nibec-entrega]')) {
          if (vistos.__entrega) return false;
          vistos.__entrega = true;
        }
        return true;
      });
  }
  // El error de un grupo de radios cuelga del grupo (fieldset), no de un radio suelto.
  function anfitrion(campo) {
    // T-1582: las casillas de entrega también cuelgan de su grupo (el error es del grupo entero, no de una casilla)
    if (campo.type === 'checkbox') return (campo.closest && campo.closest('[data-nibec-entrega]')) || campo;
    return (campo.type === 'radio' && campo.closest && campo.closest('[data-nibec-tipo], [data-nibec-entrega]')) || campo;
  }
  // aria-describedby como lista de ids: se agrega o se quita UN id sin pisar los demás (el error, la razón de un bloqueo…).
  function describir(el, id, si) {
    var resto = (el.getAttribute('aria-describedby') || '').split(' ').filter(function (x) { return x && x !== id; });
    if (si) resto.push(id);
    if (resto.length) el.setAttribute('aria-describedby', resto.join(' '));
    else el.removeAttribute('aria-describedby');
  }
  // El error de un grupo (radios o casillas) cuelga del grupo (fieldset) Y de su primer control, que es donde cae el foco al fallar: así el lector de pantalla
  // lo lee al llegar aunque no anuncie la descripción del grupo (aria-invalid en un grupo de casillas no es un atributo que todos los lectores entiendan).
  function primeroDelGrupo(h, campo) {
    return h !== campo && h.querySelector ? h.querySelector('input[type="checkbox"], input[type="radio"]') : null;
  }
  function limpiarError(campo) {
    var h = anfitrion(campo);
    h.removeAttribute('aria-invalid');
    var id = h.id + '-error';
    var previo = document.getElementById(id);
    if (previo && previo.parentNode) previo.parentNode.removeChild(previo);
    describir(h, id, false);
    var primero = primeroDelGrupo(h, campo);
    if (primero) describir(primero, id, false);
  }
  function ponerError(campo, texto, anunciar) {
    var h = anfitrion(campo);
    limpiarError(campo);
    var e = document.createElement('p');
    e.className = 'nibec-contacto__error';
    e.id = h.id + '-error';
    // T-0953 (diseño elevado): el error de la validación en línea se anuncia (el foco ya se fue a otro campo); los de «Continuar»/«Cotizar» no, porque el foco va al primer campo con error.
    if (anunciar) e.setAttribute('role', 'alert');
    e.textContent = texto;
    (h === campo ? campo.parentNode : h).appendChild(e);
    h.setAttribute('aria-invalid', 'true');
    describir(h, e.id, true);
    var primero = primeroDelGrupo(h, campo);
    if (primero) describir(primero, e.id, true);
  }
  // Con productos en la lista de cotización (nibec-cotizacion.js) el producto viaja en un campo oculto: es lo que llega en el correo, así que es la
  // fuente de verdad de «hay productos». T-1552: son obligatorios; sin ese campo con valor no se continúa ni se envía.
  function hayLista() {
    var h = form.querySelector('input[name="contact[Productos a cotizar]"]');
    return !!(h && h.value.trim());
  }

  // ---- Productos obligatorios: estado vacío y error (T-1552) -------------------------------------------------
  // Hay tres superficies con el mismo papel: la tarjeta de la columna derecha (solo se dibuja desde 990 px), el bloque dentro del formulario (para el resto de los
  // anchos) y, desde 990 px, el AVISO junto al botón (texto + salida «Explorar productos»). Con la lista vacía las tres se ven; al intentar continuar o enviar sin
  // productos se marcan en rojo y la persona oye/ve por qué:
  //   · computador: el aviso junto al botón es una región en vivo (role="status"): se anuncia sin mover el foco del botón que se acaba de apretar;
  //   · celular y tablet: el aviso queda fuera de pantalla y el foco va a la línea de error de la tarjeta de arriba (misma regla que los errores de campo:
  //     el foco va al error, por eso esa línea NO lleva role="alert"; el lector la lee al llegar).
  var vacios = document.querySelectorAll('[data-nibec-productos-vacio]');
  var ERROR_PRODUCTOS = 'Para continuar, agrega al menos un producto a tu cotización.';
  var TEXTO_SALIDA = 'Explorar productos';
  var avisoProductos = form.querySelector('[data-nibec-productos-aviso]');
  function visible(el) {
    return !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length));
  }
  // Red de seguridad: este archivo y la sección se sincronizan con Shopify por separado. Si la sección no trae el aviso ni los bloques vacíos (versión anterior en
  // vivo), el bloqueo sin productos sería mudo: aquí se arma el mismo aviso, con su salida, justo antes del botón.
  function crearAvisoDeRespaldo() {
    var caja = document.createElement('div');
    caja.className = 'nibec-contacto__aviso-productos nibec-contacto__aviso-productos--respaldo';
    caja.setAttribute('data-nibec-productos-aviso', '');
    caja.setAttribute('data-nibec-respaldo', '');
    caja.style.gridColumn = '1 / -1';
    caja.hidden = true;
    var cuerpo = document.createElement('div');
    cuerpo.className = 'nibec-contacto__aviso-cuerpo';
    var txt = document.createElement('p');
    txt.className = 'nibec-contacto__aviso-txt';
    txt.id = 'Nibec-sin-productos-respaldo';
    txt.setAttribute('data-nibec-productos-aviso-txt', '');
    txt.textContent = ERROR_PRODUCTOS;
    var salida = document.createElement('a');
    salida.className = 'nibec-contacto__aviso-link';
    salida.href = '/collections/all';
    salida.setAttribute('data-hm-id', 'contacto-aviso-catalogo');
    salida.textContent = TEXTO_SALIDA;
    cuerpo.appendChild(txt);
    cuerpo.appendChild(document.createTextNode(' '));
    cuerpo.appendChild(salida);
    var anuncio = document.createElement('span');
    anuncio.className = 'visually-hidden';
    anuncio.setAttribute('role', 'status');
    anuncio.setAttribute('data-nibec-productos-anuncio', '');
    caja.appendChild(cuerpo);
    caja.appendChild(anuncio);
    siguiente.parentNode.insertBefore(caja, siguiente);
    return caja;
  }
  function avisoTexto() {
    return avisoProductos && avisoProductos.querySelector('[data-nibec-productos-aviso-txt]');
  }
  // ¿Se está viendo el aviso junto al botón? (en celular y tablet está fuera de pantalla)
  function avisoSeVe() {
    if (!avisoProductos || avisoProductos.hidden) return false;
    var r = avisoProductos.getBoundingClientRect();
    return r.width > 8 && r.height > 8;
  }
  function anunciar(texto) {
    var region = avisoProductos && avisoProductos.querySelector('[data-nibec-productos-anuncio]');
    if (!region) return;
    region.textContent = '';
    // se escribe un instante después: así el lector de pantalla lo anuncia también cuando se repite el mismo texto
    window.setTimeout(function () { region.textContent = texto; }, 60);
  }
  function quitarErrorProductos() {
    Array.prototype.forEach.call(vacios, function (b) {
      var e = b.querySelector('.nibec-contacto__error--productos');
      if (e && e.parentNode) e.parentNode.removeChild(e);
      b.removeAttribute('data-invalido');
    });
    if (avisoProductos) {
      avisoProductos.removeAttribute('data-invalido');
      var region = avisoProductos.querySelector('[data-nibec-productos-anuncio]');
      if (region) region.textContent = '';
    }
  }
  // Marca el error en todas las superficies y devuelve el elemento al que hay que llevar el foco: la línea de error de la tarjeta que se ve, o null si lo que se
  // ve es el aviso junto al botón (ese se anuncia solo y el foco se queda en el botón).
  function ponerErrorProductos() {
    var primero = null;
    Array.prototype.forEach.call(vacios, function (b, i) {
      var previo = b.querySelector('.nibec-contacto__error--productos');
      if (previo && previo.parentNode) previo.parentNode.removeChild(previo);
      var e = document.createElement('p');
      e.className = 'nibec-contacto__error nibec-contacto__error--productos';
      e.id = 'Nibec-productos-error-' + i;
      e.tabIndex = -1;
      e.textContent = b.getAttribute('data-nibec-productos-error') || ERROR_PRODUCTOS;
      var texto = b.querySelector('.nibec-contacto__vacio-txt, .nibec-contacto__faltan-txt');
      if (texto && texto.parentNode === b) b.insertBefore(e, texto.nextSibling);
      else b.appendChild(e);
      b.setAttribute('data-invalido', 'true');
      if (!primero && visible(e)) primero = e;
    });
    if (avisoProductos) {
      avisoProductos.hidden = false;
      avisoProductos.setAttribute('data-invalido', 'true');
      if (avisoSeVe()) {
        var t = avisoTexto();
        anunciar(avisoProductos.getAttribute('data-nibec-productos-error') || (t && t.textContent.trim()) || ERROR_PRODUCTOS);
        return null;
      }
    }
    return primero;
  }
  // Muestra u oculta los bloques según haya o no productos (y limpia el error si ya hay). Con la lista vacía los botones «Continuar» y «Cotizar» se ven
  // apagados (clase is-sin-productos) y apuntan al aviso con aria-describedby: no son disabled ni aria-disabled, así que siguen recibiendo el foco y el clic muestra el error.
  function pintarVacios() {
    var hay = hayLista();
    if (!avisoProductos && !vacios.length) avisoProductos = crearAvisoDeRespaldo();
    var texto = avisoTexto();
    Array.prototype.forEach.call(vacios, function (b) { b.hidden = hay; });
    if (hay) quitarErrorProductos();
    [siguiente, enviar].forEach(function (b) {
      if (!b) return;
      b.classList.toggle('is-sin-productos', !hay);
      if (hay) {
        if (texto && b.getAttribute('aria-describedby') === texto.id) b.removeAttribute('aria-describedby');
      } else {
        if (texto && !b.hasAttribute('aria-describedby')) b.setAttribute('aria-describedby', texto.id);
      }
    });
    if (avisoProductos) avisoProductos.hidden = hay;
  }
  // Lleva un bloque a la vista dejándolo entre el encabezado fijo y la barra fija del botón (en celular el botón es una barra pegada abajo que tapa lo que queda
  // detrás: un error que quedaba justo encima del botón se leía cortado). Si el bloque no cabe completo, manda que se vea su comienzo con el encabezado arriba.
  function llevarAVista(bloque) {
    if (!bloque) return;
    var arriba = (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--ne-hdr'), 10) || 0) + 8;
    var barra = [siguiente, enviar].some(function (b) { return visible(b) && getComputedStyle(b).position === 'sticky'; });
    var abajo = window.innerHeight - (barra ? 96 : 12);
    var caja = bloque.getBoundingClientRect();
    var delta = 0;
    if (caja.bottom > abajo) delta = caja.bottom - abajo;
    if (caja.top - delta < arriba) delta = Math.max(0, caja.top - arriba);
    if (caja.top < arriba) delta = caja.top - arriba;
    if (delta) window.scrollBy({ top: delta, left: 0, behavior: reducir ? 'auto' : 'smooth' });
  }
  function enfocarProductos(e) {
    if (!e) {
      // el aviso junto al botón ya se anunció: solo se asegura que se vea
      if (avisoSeVe()) llevarAVista(avisoProductos);
      return;
    }
    try { e.focus({ preventScroll: true }); } catch (x) { e.focus(); }
    // Se lleva a la vista el bloque entero (error y botón al catálogo), no solo el error: en celular el bloque queda entre los campos y la barra fija del botón.
    llevarAVista(e.closest('[data-nibec-productos-vacio]') || e);
  }
  // Foco en el primer campo con error, y el campo con su mensaje (o el grupo de opciones entero con el suyo) a la vista por encima de la barra fija.
  function enfocarCampo(campo) {
    try { campo.focus({ preventScroll: true }); } catch (x) { campo.focus(); }
    var h = anfitrion(campo);
    llevarAVista(h === campo ? campo.parentNode || campo : h);
  }
  // nibec-cotizacion.js arma la lista (y el campo oculto) después de este archivo: se lee cuando todo cargó y cada vez que la lista cambia.
  // El evento sale antes de que el panel actualice el campo oculto, por eso se espera un instante.
  document.addEventListener('DOMContentLoaded', pintarVacios);
  window.addEventListener('load', pintarVacios);
  document.addEventListener('nibec:lista', function () { window.setTimeout(pintarVacios, 0); });
  window.addEventListener('storage', function () { window.setTimeout(pintarVacios, 0); });
  window.setTimeout(pintarVacios, 0);
  // ---- T-2032: datos obligatorios (revisión de contenido, mensajes y medición) ---------------------------------------
  var LETRAS = /[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/;
  function textoVacio(campo) {
    if (campo.hasAttribute('data-nibec-direccion')) return 'Falta la dirección. Escribe la calle y el número, por ejemplo Av. Principal 1234.';
    if (campo.hasAttribute('data-nibec-comuna')) return 'Falta la comuna. Escríbela, por ejemplo Huechuraba.';
    if (campo.getAttribute('data-brevo') === 'giro') return 'Falta el giro de la empresa. Escríbelo, por ejemplo Construcción.';
    return '';
  }
  function revisarExtra(campo, v) {
    if (campo.hasAttribute('data-nibec-direccion') && (v.length < 5 || !LETRAS.test(v))) return 'Revisa la dirección: escribe la calle y el número, por ejemplo Av. Principal 1234.';
    if (campo.hasAttribute('data-nibec-comuna') && !/^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ'’. -]{3,}$/.test(v)) return 'Revisa la comuna: escribe solo su nombre, por ejemplo Huechuraba.';
    if (campo.hasAttribute('data-nibec-razon') && v.length < 3) return 'Revisa el nombre o la razón social: escríbelo completo.';
    if (campo.getAttribute('data-brevo') === 'giro' && v.length < 3) return 'Revisa el giro: escríbelo completo, por ejemplo Construcción.';
    return '';
  }
  function idCampo(campo) {
    if (campo.hasAttribute('data-nibec-direccion')) return 'direccion';
    if (campo.hasAttribute('data-nibec-comuna')) return 'comuna';
    if (campo.hasAttribute('data-nibec-razon')) return 'razon_social';
    var b = campo.getAttribute('data-brevo');
    if (b) return b === 'razon' ? 'razon_social' : b;
    if (campo.type === 'radio' || campo.type === 'checkbox') return anfitrion(campo).hasAttribute('data-nibec-entrega') ? 'entrega' : 'tipo_cliente';
    return 'otro';
  }
  function estaVacio(campo) {
    if (campo.type === 'checkbox') return !entregaElegida();
    if (campo.type === 'radio') return !form.querySelector('input[type="radio"][name="' + campo.name.replace(/"/g, '') + '"]:checked');
    return campo.value.trim() === '';
  }
  function medirError(campo, origen) {
    if (OBL) medirErrorId(idCampo(campo), estaVacio(campo) ? 'vacio' : 'formato', origen);
  }
  function medirErrorId(id, motivo, origen) {
    if (!OBL) return;
    var datos = {
      campo: id,
      motivo: motivo,
      origen: origen,
      paso: actual,
      entrega: entregaElegida() || 'sin_elegir',
      dispositivo: window.matchMedia && window.matchMedia('(max-width: 749px)').matches ? 'celular' : 'computador'
    };
    try {
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish('nibec_cotiza_error_campo', datos);
    } catch (e) {}
    try {
      (window.dataLayer = window.dataLayer || []).push({ event: 'nibec_cotiza_error_campo', campo: datos.campo, motivo: datos.motivo, origen: datos.origen, paso: datos.paso, entrega: datos.entrega, dispositivo: datos.dispositivo });
    } catch (e) {}
  }
  function problema(campo) {
    if (campo.type === 'checkbox' && campo.closest && campo.closest('[data-nibec-entrega]')) {
      return entregaElegida() ? '' : anfitrion(campo).getAttribute('data-nibec-error') || 'Elige una opción.';
    }
    if (campo.type === 'radio') {
      var grupo = form.querySelectorAll('input[type="radio"]');
      var hay = false;
      for (var i = 0; i < grupo.length; i++) {
        if (grupo[i].name === campo.name && grupo[i].checked) hay = true;
      }
      return hay ? '' : anfitrion(campo).getAttribute('data-nibec-error') || 'Elige una opción.';
    }
    var v = campo.value.trim();
    var vacio = v === '';
    var texto = campo.getAttribute('data-nibec-error') || 'Completa este dato.';
    if (campo.required && vacio) return (OBL && textoVacio(campo)) || texto;
    if (OBL && !vacio) {
      var extra = revisarExtra(campo, v);
      if (extra) return extra;
    }
    if (campo === rut && !rutValido(v)) {
      return 'Revisa el RUT: escríbelo con su dígito verificador, por ejemplo 12.345.678-5.';
    }
    if (campo.type === 'email' && !vacio && !CORREO.test(v)) {
      return 'Revisa el correo: debe verse como nombre@empresa.cl.';
    }
    // T-0953 (diseño elevado): el teléfono solo se frena si es evidentemente corto (8 dígitos o más pasa; acepta +56, espacios y guiones).
    if (campo.type === 'tel' && !vacio && v.replace(/\D/g, '').length < 8) {
      return 'Revisa el teléfono: escríbelo con código de área o de celular, por ejemplo +56 9 1234 5678.';
    }
    return '';
  }
  // Marca todos los campos con problema del paso y devuelve el primero (o null).
  function validarPaso(paso) {
    var primero = null;
    campos(paso).forEach(function (campo) {
      var texto = problema(campo);
      if (texto) {
        ponerError(campo, texto);
        medirError(campo, 'envio');
        if (!primero) primero = campo;
      } else {
        limpiarError(campo);
      }
    });
    return primero;
  }

  // ---- Pasos ------------------------------------------------------------------------------------------------
  function medir(accion) {
    var datos = {
      paso: accion === 'volver' ? 1 : 2,
      accion: accion,
      dispositivo: window.matchMedia && window.matchMedia('(max-width: 749px)').matches ? 'celular' : 'computador'
    };
    try {
      if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish('nibec_cotizacion_paso', datos);
    } catch (e) {}
    try {
      (window.dataLayer = window.dataLayer || []).push({ event: 'nibec_cotizacion_paso', paso: datos.paso, accion: accion, dispositivo: datos.dispositivo });
    } catch (e) {}
  }

  function mostrar(n, enfocar) {
    actual = n;
    p1.hidden = n !== 1;
    p2.hidden = n !== 2;
    siguiente.hidden = n !== 1;
    volver.hidden = n !== 2;
    enviar.hidden = n !== 2;
    var paso = n === 1 ? p1 : p2;
    if (!reducir) {
      paso.classList.remove('is-entrando');
      void paso.offsetWidth;
      paso.classList.add('is-entrando');
    }
    if (enfocar) {
      var titulo = paso.querySelector('.nibec-contacto__paso-tit');
      if (titulo) {
        try { titulo.focus({ preventScroll: true }); } catch (e) { titulo.focus(); }
        // se lleva a la vista el bloque entero (con «Volver» y «Paso N de 2»), no solo el título. T-0953 (diseño elevado): solo si no se ve ya
        // (con el formulario completo en la primera pantalla la página no debe moverse al cambiar de paso).
        var caja = paso.getBoundingClientRect();
        var arriba = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--ne-hdr'), 10) || 0;
        if (caja.top < arriba || caja.top > window.innerHeight * 0.55) {
          paso.scrollIntoView({ behavior: reducir ? 'auto' : 'smooth', block: 'start' });
        }
      }
    }
  }

  function irAlPaso2() {
    var malo = validarPaso(p1);
    // T-1552: sin productos no se continúa. El error de productos se muestra junto a los de los campos; el foco va al primer campo con error y, si
    // los campos están bien, al error de productos.
    var sinProductos = !hayLista();
    var errorProductos = null;
    if (sinProductos) { errorProductos = ponerErrorProductos(); medirErrorId('productos', 'vacio', 'envio'); }
    else quitarErrorProductos();
    if (malo) {
      enfocarCampo(malo);
      return;
    }
    if (sinProductos) {
      enfocarProductos(errorProductos);
      return;
    }
    mostrar(2, true);
    medir('continuar');
  }

  siguiente.addEventListener('click', irAlPaso2);
  volver.addEventListener('click', function () {
    mostrar(1, true);
    medir('volver');
  });

  // El error se va apenas la persona corrige el campo.
  form.addEventListener('input', function (e) {
    var c = e.target;
    if (c && c.getAttribute && anfitrion(c).getAttribute('aria-invalid') === 'true') limpiarError(c);
    if (c && c.classList) c.classList.remove('is-ok');
  });
  // T-0953 (diseño elevado, CW-LEAD-1): validación en línea. Al SALIR de un campo con algo escrito se avisa si está mal (mensaje en el campo, con
  // salida) o se marca con un visto verde; un campo vacío no se castiga hasta «Continuar» o «Cotizar» (premiar pronto, avisar tarde).
  form.addEventListener('focusout', function (e) {
    var c = e.target;
    if (!c || !c.matches || !c.matches('input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]), textarea')) return;
    if (c.disabled || !c.value.trim()) { c.classList.remove('is-ok'); return; }
    var texto = problema(c);
    if (texto) { c.classList.remove('is-ok'); ponerError(c, texto, true); medirError(c, 'salida'); }
    else { limpiarError(c); if (c.tagName !== 'TEXTAREA') c.classList.add('is-ok'); }
  });
  // ---- «¿Persona natural o empresa?» (solo si el ajuste está encendido) --------------------------------------
  var tipo = form.querySelector('[data-nibec-tipo]');
  if (tipo) {
    var razon = form.querySelector('[data-nibec-razon]');
    var razonEtiqueta = form.querySelector('[data-nibec-razon-label]');
    var giroBloque = form.querySelector('[data-nibec-giro]');
    var giro = giroBloque && giroBloque.querySelector('input');
    var POR_TIPO = {
      'Persona natural': { etiqueta: 'Nombre completo', ayuda: 'Tu nombre y apellido', auto: 'name', giro: false },
      Empresa: { etiqueta: 'Razón social', ayuda: 'Razón social de tu empresa', auto: 'organization', giro: true }
    };
    var aplicarTipo = function () {
      var marcado = tipo.querySelector('input[type="radio"]:checked');
      var t = marcado && POR_TIPO[marcado.value];
      if (!t || !razon || !razonEtiqueta || !giro || !giroBloque) return;
      razonEtiqueta.textContent = t.etiqueta;
      razon.placeholder = t.ayuda;
      razon.setAttribute('autocomplete', t.auto);
      giroBloque.hidden = !t.giro;
      giro.disabled = !t.giro;
      giro.required = t.giro;
      if (!t.giro) limpiarError(giro);
    };
    tipo.addEventListener('change', aplicarTipo);
    aplicarTipo(); // el navegador puede devolver la página con una opción ya marcada
  }
  // ---- Entrega (T-1552, casillas desde T-1582) ---------------------------------------------------------------
  // Cuatro casillas (data-entrega): despacho_santiago, armado_santiago, retiro_tienda y despacho_region. Reglas (ver la cabecera): el armado en Santiago incluye
  // SIEMPRE el despacho (se marca solo y queda bloqueado mientras el armado esté marcado); retiro y región son excluyentes entre sí y con las de Santiago; al menos
  // una. El código que viaja al Worker: despacho_santiago | despacho_armado_santiago | retiro_tienda | despacho_region. Sin elección no se muestran dirección ni
  // comuna; con cualquier opción menos «Retiro en tienda» se piden; con «Retiro en tienda» quedan ocultos y deshabilitados (un campo deshabilitado no se valida
  // ni viaja en el correo). Lo escrito se conserva si la persona cambia de opinión. La nota bajo las opciones (data-nota) es una región en vivo.
  // Si la página trae la sección anterior (radios con data-entrega), todo sigue como antes: el código sale tal cual (incluido `armado_santiago`).
  var entrega = form.querySelector('[data-nibec-entrega]');
  var entregaCampos = form.querySelector('[data-nibec-entrega-campos]');
  var entregaNota = form.querySelector('[data-nibec-entrega-nota]');
  var entregaCopia = form.querySelector('[data-nibec-brevo-entrega]');
  var entregaValor = form.querySelector('[data-nibec-entrega-valor]'); // contact[Entrega] oculto: el texto legible que va en el correo
  var entregaAnuncio = form.querySelector('[data-nibec-entrega-anuncio]'); // aviso solo para lector de pantalla (cambios que hace el formulario solo)
  var conCasillas = !!(entrega && entrega.querySelector('input[type="checkbox"][data-entrega]'));
  var ENTREGAS = { despacho_santiago: 1, armado_santiago: 1, despacho_armado_santiago: 1, retiro_tienda: 1, despacho_region: 1 };
  var TEXTO_ENTREGA = {
    despacho_santiago: 'Despacho en Santiago',
    despacho_armado_santiago: 'Despacho y armado en Santiago',
    retiro_tienda: 'Retiro en tienda',
    despacho_region: 'Despacho a Región'
  };
  function casilla(codigo) {
    return entrega ? entrega.querySelector('input[type="checkbox"][data-entrega="' + codigo + '"]') : null;
  }
  function marcada(codigo) {
    var c = casilla(codigo);
    return !!(c && c.checked);
  }
  function entregaMarcada() {
    return entrega && entrega.querySelector('input[type="radio"]:checked');
  }
  function entregaElegida() {
    if (!entrega) return '';
    if (conCasillas) {
      // el armado en Santiago siempre lleva el despacho: juntos son un solo código
      if (marcada('retiro_tienda')) return 'retiro_tienda';
      if (marcada('despacho_region')) return 'despacho_region';
      if (marcada('armado_santiago')) return 'despacho_armado_santiago';
      if (marcada('despacho_santiago')) return 'despacho_santiago';
      return '';
    }
    var marcado = entregaMarcada();
    var v = marcado ? marcado.getAttribute('data-entrega') : '';
    return ENTREGAS[v] ? v : '';
  }
  var anuncioPendiente = 0;
  function anunciarEntrega(texto) {
    if (!entregaAnuncio || !texto) return;
    // vaciar y llenar en otro turno: la región en vivo repite el aviso aunque el texto sea el mismo
    entregaAnuncio.textContent = '';
    window.clearTimeout(anuncioPendiente);
    anuncioPendiente = window.setTimeout(function () { entregaAnuncio.textContent = texto; }, 30);
  }
  // Aplica las reglas de las casillas. `tocada` es la que la persona acaba de cambiar (null al cargar la página, p. ej. cuando el navegador restaura el formulario).
  // Devuelve el aviso (texto) de lo que el formulario cambió solo, o ''.
  function ordenarCasillas(tocada) {
    var d = casilla('despacho_santiago');
    var a = casilla('armado_santiago');
    var r = casilla('retiro_tienda');
    var g = casilla('despacho_region');
    if (!d || !a || !r || !g) return '';
    var quitadas = [];
    function quitar(lista) {
      lista.forEach(function (c) {
        if (c.checked) {
          c.checked = false;
          quitadas.push(c.getAttribute('data-texto') || '');
        }
      });
    }
    var aviso = '';
    if (tocada === r && r.checked) quitar([d, a, g]);
    else if (tocada === g && g.checked) quitar([d, a, r]);
    else if ((tocada === d || tocada === a) && (d.checked || a.checked)) quitar([r, g]);
    else if (!tocada) {
      // formulario restaurado con una mezcla imposible: manda retiro, después región
      if (r.checked) quitar([d, a, g]);
      else if (g.checked) quitar([d, a]);
    }
    if (quitadas.length) aviso = 'Se desmarcó ' + quitadas.join(' y ') + '.';
    // armado en Santiago = despacho en Santiago incluido
    if (a.checked && !d.checked) {
      d.checked = true;
      aviso = 'Armado en Santiago marcado: el despacho en Santiago se marcó también.';
    }
    return aviso;
  }
  function aplicarEntrega() {
    if (!entrega) return;
    var v = entregaElegida();
    if (entregaCopia) entregaCopia.value = v;
    if (conCasillas) {
      if (entregaValor) entregaValor.value = TEXTO_ENTREGA[v] || '';
      // «Despacho en Santiago» queda bloqueado (aria-disabled, con foco y con su razón) mientras el armado esté marcado
      var d = casilla('despacho_santiago');
      var etiqueta = d && d.closest('label');
      var tag = etiqueta && etiqueta.querySelector('.nibec-contacto__radio-tag');
      var bloqueado = marcada('armado_santiago');
      if (d) {
        if (bloqueado) {
          d.setAttribute('aria-disabled', 'true');
          if (tag && tag.id) describir(d, tag.id, true);
          if (entregaNota && entregaNota.id) describir(d, entregaNota.id, true);
        } else {
          d.removeAttribute('aria-disabled');
          if (tag && tag.id) describir(d, tag.id, false);
          if (entregaNota && entregaNota.id) describir(d, entregaNota.id, false);
        }
      }
    }
    var conNota = entrega.querySelectorAll('input:checked[data-nota]');
    var nota = '';
    for (var i = 0; i < conNota.length && !nota; i++) nota = conNota[i].getAttribute('data-nota') || '';
    if (entregaNota) entregaNota.textContent = nota;
    if (!entregaCampos) return;
    // T-2032: con «Datos obligatorios» la dirección y la comuna se piden SIEMPRE (también con retiro en tienda): quedan visibles y habilitadas, y lo escrito se conserva.
    var pedir = OBL || (v !== '' && v !== 'retiro_tienda');
    entregaCampos.hidden = !pedir;
    Array.prototype.forEach.call(entregaCampos.querySelectorAll('input'), function (c) {
      c.disabled = !pedir;
      if (!pedir) limpiarError(c);
    });
    if (OBL) {
      var etiquetaDir = entregaCampos.querySelector('[data-nibec-direccion-label]');
      if (etiquetaDir) etiquetaDir.textContent = v === 'retiro_tienda' ? 'Tu dirección (para la cotización)' : 'Dirección de despacho';
    }
  }
  if (entrega) {
    if (conCasillas) {
      // «Despacho en Santiago» bloqueada: el clic (o la barra espaciadora) no la desmarca; se dice por qué en vez de callar.
      entrega.addEventListener('click', function (e) {
        var c = e.target;
        if (c && c.getAttribute && c.type === 'checkbox' && c.getAttribute('aria-disabled') === 'true') {
          e.preventDefault();
          anunciarEntrega('El despacho en Santiago va con el armado. Para quitarlo, desmarca el armado en Santiago.');
        }
      });
      entrega.addEventListener('change', function (e) {
        var aviso = ordenarCasillas(e.target);
        aplicarEntrega();
        anunciarEntrega(aviso);
      });
      ordenarCasillas(null); // el navegador puede devolver la página con casillas ya marcadas
    } else {
      entrega.addEventListener('change', aplicarEntrega);
    }
    aplicarEntrega();
  }

  // El RUT queda escrito como 12.345.678-5 al salir del campo.
  rut.addEventListener('blur', function () {
    if (rut.value.trim()) rut.value = formatearRut(rut.value);
  });

  // T-0953 (diseño elevado): al enviar de verdad, el botón muestra que está trabajando (giro + «Enviando tu cotización…») y un aviso para el lector de
  // pantalla. Si la página no cambia (p. ej. se cierra el desafío del captcha), a los 25 s el botón vuelve a su estado y se puede reintentar.
  var textoEnviar = '';
  var estadoEnviar = null;
  function marcarEnviando() {
    if (enviar.classList.contains('is-enviando')) return;
    textoEnviar = enviar.textContent.replace(/\s+/g, ' ').trim();
    enviar.classList.add('is-enviando');
    enviar.setAttribute('aria-busy', 'true');
    enviar.textContent = '';
    var giro = document.createElement('span');
    giro.className = 'ne-giro';
    giro.setAttribute('aria-hidden', 'true');
    var t = document.createElement('span');
    t.textContent = 'Enviando tu cotización…';
    enviar.appendChild(giro);
    enviar.appendChild(t);
    if (!estadoEnviar) {
      estadoEnviar = document.createElement('p');
      estadoEnviar.className = 'visually-hidden';
      estadoEnviar.setAttribute('role', 'status');
      form.appendChild(estadoEnviar);
    }
    estadoEnviar.textContent = 'Enviando tu cotización.';
    window.setTimeout(function () {
      if (!enviar.classList.contains('is-enviando')) return;
      enviar.classList.remove('is-enviando');
      enviar.removeAttribute('aria-busy');
      enviar.textContent = textoEnviar;
      if (estadoEnviar) estadoEnviar.textContent = '';
    }, 25000);
  }

  // Va en la fase de captura de window, antes que cualquier otro listener: Shopify pone en el formulario un listener de
  // hCaptcha que hace preventDefault, corre el desafío y envía después con el submit nativo (sin validar nada). Si
  // este bloqueo corriera después, el desafío arrancaría igual y el formulario saldría incompleto. Con
  // stopImmediatePropagation el envío bloqueado no llega ni al captcha ni a nibec-brevo.js (la copia para Brevo solo
  // se guarda cuando el envío es de verdad).
  window.addEventListener(
    'submit',
    function (e) {
      if (e.target !== form) return;
      if (actual === 1) {
        // Enter dentro del paso 1: es «Continuar», no un envío.
        e.preventDefault();
        e.stopImmediatePropagation();
        irAlPaso2();
        return;
      }
      var malo1 = validarPaso(p1);
      var malo2 = validarPaso(p2);
      // T-1552: la lista se puede vaciar desde la columna derecha estando en el paso 2; sin productos tampoco se envía.
      var sinProductos = !hayLista();
      var errorProductos = null;
      if (sinProductos) { errorProductos = ponerErrorProductos(); medirErrorId('productos', 'vacio', 'envio'); }
      else quitarErrorProductos();
      if (malo1 || malo2 || sinProductos) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (malo1) {
          mostrar(1, false);
          enfocarCampo(malo1);
        } else if (malo2) {
          enfocarCampo(malo2);
        } else {
          enfocarProductos(errorProductos);
        }
        return;
      }
      rut.value = formatearRut(rut.value);
      aplicarEntrega();
      var direccion = form.querySelector('[data-nibec-direccion]');
      var comuna = form.querySelector('[data-nibec-comuna]');
      var copia = form.querySelector('[data-nibec-brevo-direccion]');
      if (copia) {
        // Con «Retiro en tienda» no hay dirección ni comuna (los campos están deshabilitados y no viajan): la copia para Brevo y el Worker queda vacía.
        var conDireccion = OBL || !entrega || entregaElegida() !== 'retiro_tienda';
        copia.value = conDireccion ? [direccion && direccion.value.trim(), comuna && comuna.value.trim()].filter(Boolean).join(', ') : '';
      }
      medir('enviar');
      marcarEnviando();
    },
    true
  );

  // ---- Borrador del paso 1 (T-1552, 2.ª parte) ----------------------------------------------------------------
  // La salida de los avisos («Explorar productos») lleva a otra página: lo escrito (nombre, correo, teléfono e información adicional) se guarda en esta pestaña
  // (sessionStorage: no sale del navegador y se borra al cerrarla) y vuelve al regresar. Solo se rellenan campos vacíos. Se borra cuando la página vuelve del
  // envío exitoso (arriba de todo) o cuando la persona deja los cuatro campos vacíos.
  var CLAVES_BORRADOR = ['nombre', 'email', 'telefono', 'mensaje'];
  function camposBorrador() {
    return CLAVES_BORRADOR.map(function (k) { return [k, p1.querySelector('[data-brevo="' + k + '"]')]; }).filter(function (x) { return x[1]; });
  }
  function guardarBorrador() {
    try {
      var d = {};
      camposBorrador().forEach(function (x) { if (x[1].value.trim()) d[x[0]] = x[1].value; });
      if (Object.keys(d).length) sessionStorage.setItem(BORRADOR, JSON.stringify(d));
      else sessionStorage.removeItem(BORRADOR);
    } catch (e) {}
  }
  try {
    var guardado = JSON.parse(sessionStorage.getItem(BORRADOR) || '{}') || {};
    camposBorrador().forEach(function (x) {
      if (typeof guardado[x[0]] === 'string' && !x[1].value.trim()) x[1].value = guardado[x[0]];
    });
  } catch (e) {}
  form.addEventListener('input', guardarBorrador);

  mostrar(1, false);
})();
